import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

interface Gtm {
  key: string;
  baseUrl: string;
  gtmUrl: string;
}

@Injectable({ providedIn: 'root' })
export class ScriptInjectorService {
  private injected = false;

  private readonly optimizelyUrl = 'https://cdn.optimizely.com/js/22785643751.js';
  private readonly gtm: Gtm;
  private readonly gtmTotalEnergies: Gtm;
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    this.gtm = {
      key: environment.googleTagManager,
      baseUrl: 'https://www.googletagmanager.com',
      gtmUrl: 'https://www.googletagmanager.com/gtm.js',
    };

    this.gtmTotalEnergies = {
      key: environment.googleTagManagerTotalEnergies,
      baseUrl: 'https://analytics.totalenergies.be',
      gtmUrl: 'https://analytics.totalenergies.be/swki9m7euijf6nx.js',
    };
  }

  injectScripts(): void {
    if (!isPlatformBrowser(this.platformId) || this.injected) {
      return;
    }

    this.injected = true;
    this.injectDataLayerInit();

    // GTM scripts
    this.injectGTMScript(this.gtm.key, this.gtm.gtmUrl);
    this.injectGTMScript(this.gtmTotalEnergies.key, this.gtmTotalEnergies.gtmUrl);

    // Optimizely script
    this.injectOptimizelyInit();
    this.injectScript(this.optimizelyUrl);

    // NoScripts
    this.injectNoScriptIframes(this.gtm.key, this.gtm.baseUrl);
    this.injectNoScriptIframes(this.gtmTotalEnergies.key, this.gtmTotalEnergies.baseUrl);
  }

  private injectDataLayerInit(): void {
    const script = document.createElement('script');
    script.textContent = 'dataLayer = [];';
    document.head.appendChild(script);
  }

  private injectScript(url: string): void {
    const script = document.createElement('script');
    script.src = url;
    document.head.appendChild(script);
  }
  private injectGTMScript(gtmId: string, baseUrl: string): void {
    const script = document.createElement('script');

    const isTotalEnergies = gtmId === this.gtmTotalEnergies.key;
    const srcTemplate = isTotalEnergies
      ? `"${baseUrl}?awl=" + i.replace(/^GTM-/, "") + dl`
      : `'${baseUrl}?id=' + i + dl`;

    script.textContent = `
      (function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
          var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != "dataLayer" ? "&l=" + l : "";
          j.async = true;
          j.src = ${srcTemplate};
          f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", "${gtmId}");
    `;

    document.head.appendChild(script);
  }
  private injectOptimizelyInit(): void {
    const script = document.createElement('script');
    script.textContent = `
        window.optimizely = window.optimizely || [];
        if (!document.cookie.includes('optimizelyAllowDataSharing=true')) {
                window.optimizely.push(
                        {       type: 'holdEvents'     }
                );
              }
    `;
    document.head.appendChild(script);
  }

  private injectNoScriptIframes(gtmId: string, baseUrl: string): void {
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="${baseUrl}/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }
}
